/**
 * AI Readiness Lead Magnet Prompt
 *
 * Reusable marketing surface that explains the company-email readiness
 * flow and opens the modal instead of routing away.
 *
 * Primary responsibilities:
 * - Present AI readiness messaging on enterprise and pricing surfaces
 * - Reuse the shared modal CTA without embedding another large form
 *
 * Dependencies:
 * - generated marketing asset
 * - lead magnet modal
 * - button
 */

'use client'

import { ArrowRightIcon } from '@radix-ui/react-icons'
import { useTranslation } from 'react-i18next'

import { GeneratedMarketingAsset } from '@/components/marketing/generated-marketing-asset'
import { AiReadinessLeadMagnetModal } from '@/components/quick-ai-readiness/lead-magnet-modal'
import { Button } from '@/components/ui/button'
import { getMarketingCopy } from '@/lib/marketing-copy'
import {
  selectedGeneratedAssets,
  type SelectedGeneratedAssetKey
} from '@/lib/marketing/selected-generated-assets'
import { AiReadinessLeadMagnetSource } from '@/lib/quick-ai-readiness/lead-magnet'
import { cn } from '@/lib/utils'

interface AiReadinessLeadMagnetPromptProps {
  assetKey?: SelectedGeneratedAssetKey
  className?: string
  description?: string
  eyebrow?: string
  items?: string[]
  source: AiReadinessLeadMagnetSource
  title?: string
  variant?: 'panel' | 'section'
}

export function AiReadinessLeadMagnetPrompt({
  assetKey,
  className,
  description,
  eyebrow,
  items,
  source,
  title,
  variant = 'section'
}: AiReadinessLeadMagnetPromptProps) {
  const { i18n } = useTranslation()
  const copy = getMarketingCopy(i18n.resolvedLanguage || i18n.language)
  const isPanelVariant = variant === 'panel'
  const resolvedAssetKey =
    assetKey || (isPanelVariant ? 'aiReadinessAnalysis' : 'aiReadinessReport')
  const resolvedItems =
    items ||
    (isPanelVariant
      ? copy.aiReadiness.trustPoints.map((item) => item.title)
      : copy.leadMagnet.afterSubmitSteps)
  const resolvedEyebrow = eyebrow || copy.leadMagnet.label
  const resolvedTitle =
    title ||
    (isPanelVariant ? copy.enterprise.includes.readinessTitle : copy.leadMagnet.title)
  const resolvedDescription =
    description ||
    (isPanelVariant
      ? copy.enterprise.includes.readinessDescription
      : copy.leadMagnet.description)

  if (isPanelVariant) {
    return (
      <div className={cn('flex h-full flex-col gap-6', className)}>
        <div className="relative overflow-hidden rounded-[1.75rem] border border-slate-200/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.94),rgba(241,245,250,0.9))] dark:border-white/10 dark:bg-[linear-gradient(180deg,rgba(15,22,32,0.98),rgba(8,13,20,0.94))]">
          <GeneratedMarketingAsset
            assetKey={resolvedAssetKey}
            className="aspect-[16/10] min-h-[250px]"
            sizes="(min-width: 1024px) 520px, 100vw"
          />
        </div>

        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
            {resolvedEyebrow}
          </p>
          <h2 className="mt-3 font-display text-3xl font-medium leading-[0.98] tracking-[-0.02em] text-slate-950 dark:text-slate-50">
            {resolvedTitle}
          </h2>
          <p className="mt-4 text-sm leading-7 text-slate-600 dark:text-slate-300">
            {resolvedDescription}
          </p>
        </div>

        <div className="grid gap-3">
          {resolvedItems.slice(0, 3).map((item, index) => (
            <div
              key={item}
              className="rounded-[1.4rem] border border-slate-200/80 bg-white/72 px-4 py-3 dark:border-white/10 dark:bg-white/[0.04]"
            >
              <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                {`0${index + 1}`}
              </p>
              <p className="mt-2 text-sm leading-6 text-slate-700 dark:text-slate-200">
                {item}
              </p>
            </div>
          ))}
        </div>

        <AiReadinessLeadMagnetModal source={source}>
          <Button className="mt-auto h-12 rounded-full bg-slate-950 px-6 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-950 dark:hover:bg-white">
            {copy.common.checkAiReadiness}
            <ArrowRightIcon className="ml-2 size-4" />
          </Button>
        </AiReadinessLeadMagnetModal>
      </div>
    )
  }

  const asset = selectedGeneratedAssets[resolvedAssetKey]

  return (
    <section
      className={cn(
        'overflow-hidden rounded-[2rem] border border-slate-200/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.96),rgba(243,247,251,0.94))] shadow-[0_24px_80px_rgba(15,23,42,0.08)] dark:border-white/10 dark:bg-[linear-gradient(180deg,rgba(11,16,24,0.98),rgba(5,10,16,0.96))] dark:shadow-[0_24px_80px_rgba(0,0,0,0.34)]',
        className
      )}
    >
      <div className="grid gap-0 lg:grid-cols-[0.9fr_1.1fr]">
        <div className="relative overflow-hidden border-b border-slate-200/80 p-4 sm:p-5 lg:border-b-0 lg:border-r dark:border-white/10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(190,204,224,0.24),transparent_52%)] dark:bg-[radial-gradient(circle_at_top,rgba(65,90,126,0.18),transparent_52%)]" />
          <GeneratedMarketingAsset
            assetKey={resolvedAssetKey}
            className={cn(
              'aspect-[16/10] min-h-[280px] sm:min-h-[320px]',
              asset.fit === 'cover' ? undefined : 'sm:scale-[1.02]'
            )}
            sizes="(min-width: 1024px) 520px, 100vw"
          />
        </div>

        <div className="p-6 sm:p-8">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
            {resolvedEyebrow}
          </p>
          <h2 className="mt-3 font-display max-w-[14ch] text-4xl font-medium leading-[0.98] tracking-[-0.02em] text-slate-950 dark:text-slate-50">
            {resolvedTitle}
          </h2>
          <p className="mt-4 max-w-[56ch] text-base leading-8 text-slate-600 dark:text-slate-300">
            {resolvedDescription}
          </p>

          <div className="mt-6 grid gap-3">
            {resolvedItems.slice(0, 3).map((item, index) => (
              <div
                key={item}
                className="rounded-[1.4rem] border border-slate-200/80 bg-white/74 px-4 py-3 dark:border-white/10 dark:bg-white/[0.04]"
              >
                <div className="flex items-start gap-4">
                  <span className="mt-0.5 inline-flex size-8 shrink-0 items-center justify-center rounded-full border border-slate-200/80 bg-white text-[11px] font-semibold tracking-[0.18em] text-slate-500 dark:border-white/10 dark:bg-white/5 dark:text-slate-300">
                    {`0${index + 1}`}
                  </span>
                  <p className="text-sm leading-6 text-slate-700 dark:text-slate-200">
                    {item}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <AiReadinessLeadMagnetModal source={source}>
            <Button className="mt-6 h-12 rounded-full bg-slate-950 px-6 text-white hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-950 dark:hover:bg-white">
              {copy.common.checkAiReadiness}
              <ArrowRightIcon className="ml-2 size-4" />
            </Button>
          </AiReadinessLeadMagnetModal>
        </div>
      </div>
    </section>
  )
}
